/*
 * -------------------------------------------------------------------------------------------------------------------
 * Copyright (c) 2025 Christian Bannard
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * 
 *     http://www.apache.org/licenses/LICENSE-2.0
 * 
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * -------------------------------------------------------------------------------------------------------------------
 * File: c:\localdev\templaredge\frontend\src\pages\Tasks\TaskCreate.tsx
 *  
 * Author: Christian Bannard
 * Created: 2025-11-23
 *  
 * Description: Task creation and editing form with patient association, user assignment, and attachments
 * -------------------------------------------------------------------------------------------------------------------
 * HISTORY:
 * Date      	By	Comments
 * ----------	---	-------------------------------------------------------------------------------------------------
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { apiCall, ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { Task, Patient, User, TaskAttachment } from '../../types/task';
import { Document } from '../../types';
import { 
	FiSave, 
	FiX, 
	FiUpload, 
	FiFile, 
	FiTrash2, 
	FiUser, 
	FiPaperclip,
	FiFolder,
	FiDownload
} from 'react-icons/fi';

interface LocationState {
	patient?: Patient;
	task?: Task;
}

const TaskCreate: React.FC = () => {
	const { token } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const { id: taskId } = useParams<{ id: string }>();
	const fileInputRef = useRef<HTMLInputElement>(null);
	
	// Determine if editing
	const isEditing = !!taskId;
	const locationState = location.state as LocationState;
	
	// Form state
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
	const [status, setStatus] = useState<'open' | 'in_progress' | 'completed' | 'cancelled'>('open');
	const [dueDate, setDueDate] = useState('');
	
	// Patient association
	const [selectedPatient, setSelectedPatient] = useState<Patient | null>(locationState?.patient || null);
	const [patientSearch, setPatientSearch] = useState('');
	const [patientResults, setPatientResults] = useState<Patient[]>([]);
	const [showPatientResults, setShowPatientResults] = useState(false);
	
	// User assignment
	const [assignedToId, setAssignedToId] = useState<number | null>(null);
	const [users, setUsers] = useState<User[]>([]);
	const [loadingUsers, setLoadingUsers] = useState(false);
	
	// Attachments
	const [attachments, setAttachments] = useState<(File | TaskAttachment)[]>([]);
	
	// Patient documents
	const [patientDocuments, setPatientDocuments] = useState<Document[]>([]);
	const [loadingDocuments, setLoadingDocuments] = useState(false);
	const [documentFilter, setDocumentFilter] = useState('');
	const [showDocumentSelector, setShowDocumentSelector] = useState(false);
	
	// UI state
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Load task if editing
	useEffect(() => {
		if (isEditing && taskId && token) {
			loadTask();
		}
	}, [isEditing, taskId, token]);

	// Load users for assignment
	useEffect(() => {
		if (token) {
			loadUsers();
		}
	}, [token]);

	// Patient search with debounce
	useEffect(() => {
		if (patientSearch.length >= 2 && token && !selectedPatient) {
			const timer = setTimeout(async () => {
				try {
					const response = await apiCall(
						`${ENDPOINTS.PATIENTS.PATIENTS}?search=${encodeURIComponent(patientSearch)}`,
						{ method: 'GET' },
						token
					);

					if (response.ok) {
						const data = await response.json();
						const patients = data.results || data || [];

						const searchLower = patientSearch.toLowerCase().trim();
						const filtered = patients.filter((patient: Patient) => {
							const fullName = `${patient.first_name} ${patient.last_name}`.toLowerCase();
							const reverseName = `${patient.last_name} ${patient.first_name}`.toLowerCase();
							const mrn = (patient.medical_record_number || '').toLowerCase();

							return fullName.includes(searchLower) ||
								reverseName.includes(searchLower) ||
								mrn.includes(searchLower);
						});

						setPatientResults(filtered);
						setShowPatientResults(filtered.length > 0);
					}
				} catch (error) {
					console.error('Error searching patients:', error);
				}
			}, 300);

			return () => clearTimeout(timer);
		} else if (selectedPatient) {
			setShowPatientResults(false);
		} else {
			setPatientResults([]);
			setShowPatientResults(false);
		}
	}, [patientSearch, token, selectedPatient]);

	// Load patient documents when patient is selected
	useEffect(() => {
		if (selectedPatient && token) {
			loadPatientDocuments();
		} else {
			setPatientDocuments([]);
		}
	}, [selectedPatient, token]);

	const loadTask = async () => {
		if (!token || !taskId) return;

		setLoading(true);
		setError(null);

		try {
			const response = await apiCall(
				`${ENDPOINTS.TASKS.DETAIL}${taskId}/`,
				{ method: 'GET' },
				token
			);

			if (!response.ok) {
				throw new Error('Failed to load task');
			}

			const task: Task = await response.json();
			
			setTitle(task.title);
			setDescription(task.description || '');
			setPriority(task.priority);
			setStatus(task.status);
			setDueDate(task.due_date || '');
			setAssignedToId(task.assigned_to_id || null);

			if (task.patient_id) {
				// Load patient details
				const patientResponse = await apiCall(
					`${ENDPOINTS.PATIENTS.PATIENTS}${task.patient_id}/`,
					{ method: 'GET' },
					token
				);

				if (patientResponse.ok) {
					const patient = await patientResponse.json();
					setSelectedPatient(patient);
					setPatientSearch(`${patient.first_name} ${patient.last_name}`);
				}
			}

			// Load existing attachments
			loadTaskAttachments(taskId);
		} catch (err) {
			console.error('Error loading task:', err);
			setError('Failed to load task');
		} finally {
			setLoading(false);
		}
	};

	const loadTaskAttachments = async (taskId: string) => {
		if (!token) return;

		try {
			const response = await apiCall(
				`${ENDPOINTS.TASKS.ATTACHMENTS}${taskId}/attachments/`,
				{ method: 'GET' },
				token
			);

			if (response.ok) {
				const data = await response.json();
				setAttachments(data.results || data || []);
			}
		} catch (err) {
			console.error('Error loading attachments:', err);
		}
	};

	const loadUsers = async () => {
		if (!token) return;

		setLoadingUsers(true);

		try {
			const response = await apiCall(
				ENDPOINTS.USERS.LIST,
				{ method: 'GET' },
				token
			);

			if (response.ok) {
				const data = await response.json();
				setUsers(data.results || data || []);
			}
		} catch (err) {
			console.error('Error loading users:', err);
		} finally {
			setLoadingUsers(false);
		}
	};

	const loadPatientDocuments = async () => {
		if (!token || !selectedPatient) return;

		setLoadingDocuments(true);

		try {
			const response = await apiCall(
				`${ENDPOINTS.DOCUMENTS.LIST}?patient_id=${selectedPatient.id}`,
				{ method: 'GET' },
				token
			);

			if (response.ok) {
				const data = await response.json();
				setPatientDocuments(data.results || data || []);
			}
		} catch (err) {
			console.error('Error loading patient documents:', err);
		} finally {
			setLoadingDocuments(false);
		}
	};

	const handlePatientSelect = (patient: Patient) => {
		setSelectedPatient(patient);
		setPatientSearch(`${patient.first_name} ${patient.last_name}`);
		setShowPatientResults(false);
		setPatientResults([]);
	};

	const clearPatientSelection = () => {
		setSelectedPatient(null);
		setPatientSearch('');
		setPatientResults([]);
		setShowPatientResults(false);
		setPatientDocuments([]);
	};

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files) return;

		const newFiles = Array.from(files);
		setAttachments([...attachments, ...newFiles]);
	};

	const handleRemoveAttachment = (index: number) => {
		setAttachments(attachments.filter((_, i) => i !== index));
	};

	const handleAddPatientDocument = (doc: Document) => {
		// Check if already added
		const exists = attachments.some(att => 
			'document_id' in att && (att as TaskAttachment).document_id === doc.id
		);

		if (exists) {
			alert('This document is already attached');
			return;
		}

		// Add as TaskAttachment reference
		const attachment: Partial<TaskAttachment> = {
			file_name: doc.title,
			file_size: 0,
			file_type: 'document',
			document_id: doc.id,
			is_from_patient_file: true,
		};

		setAttachments([...attachments, attachment as any]);
		setShowDocumentSelector(false);
	};

	const formatFileSize = (bytes: number): string => {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
	};

	const handleSave = async () => {
		if (!token) return;

		// Validation
		if (!title.trim()) {
			alert('Please enter a task title');
			return;
		}

		setSaving(true);
		setError(null);

		try {
			const taskData = {
				title: title.trim(),
				description: description.trim() || undefined,
				priority,
				status: isEditing ? status : undefined,
				patient_id: selectedPatient?.id || undefined,
				assigned_to_id: assignedToId || undefined,
				due_date: dueDate || undefined,
			};

			// Create or update task
			const taskResponse = await apiCall(
				isEditing ? `${ENDPOINTS.TASKS.UPDATE}${taskId}/` : ENDPOINTS.TASKS.CREATE,
				{
					method: isEditing ? 'PATCH' : 'POST',
					body: JSON.stringify(taskData),
				},
				token
			);

			if (!taskResponse.ok) {
				const errorData = await taskResponse.json();
				throw new Error(errorData.message || 'Failed to save task');
			}

			const savedTask = await taskResponse.json();

			// Upload attachments (only new File objects)
			const newFiles = attachments.filter(att => att instanceof File) as File[];
			
			if (newFiles.length > 0) {
				const formData = new FormData();
				newFiles.forEach((file, index) => {
					formData.append(`file_${index}`, file);
				});

				await apiCall(
					`${ENDPOINTS.TASKS.ATTACHMENTS}${savedTask.id}/attachments/`,
					{
						method: 'POST',
						body: formData,
					},
					token
				);
			}

			// Link patient documents
			const documentAttachments = attachments.filter(
				att => 'document_id' in att
			) as TaskAttachment[];

			for (const docAtt of documentAttachments) {
				if (docAtt.document_id) {
					await apiCall(
						`${ENDPOINTS.TASKS.ATTACHMENTS}${savedTask.id}/attachments/link-document/`,
						{
							method: 'POST',
							body: JSON.stringify({ document_id: docAtt.document_id }),
						},
						token
					);
				}
			}

			// If task has a patient and user uploaded files, ask about adding to patient file
			if (selectedPatient && newFiles.length > 0) {
				const shouldAddToPatient = window.confirm(
					`This task has ${newFiles.length} attachment(s). Would you like to add these to ${selectedPatient.first_name} ${selectedPatient.last_name}'s patient file?`
				);

				if (shouldAddToPatient) {
					// TODO: Implement adding files to patient document storage
					console.log('Would add files to patient document storage:', newFiles);
				}
			}

			window.dispatchEvent(new CustomEvent('tasks:refresh'));
			navigate('/tasks');
		} catch (err) {
			console.error('Error saving task:', err);
			setError(err instanceof Error ? err.message : 'Failed to save task');
		} finally {
			setSaving(false);
		}
	};

	const handleCancel = () => {
		if (window.confirm('Are you sure you want to discard changes?')) {
			navigate('/tasks');
		}
	};

	const filteredDocuments = patientDocuments.filter(doc =>
		doc.title.toLowerCase().includes(documentFilter.toLowerCase())
	);

	if (loading) {
		return (
			<div className="h-full flex items-center justify-center">
				<span className="loading loading-spinner loading-lg"></span>
			</div>
		);
	}

	return (
		<div className="h-full flex flex-col">
			{/* Header */}
			<div className="bg-base-200 p-6 shadow-lg flex-shrink-0">
				<div className="flex justify-between items-center">
					<h1 className="text-2xl font-bold">
						{isEditing ? 'Edit Task' : 'Create Task'}
					</h1>
					<div className="flex gap-2">
						<button onClick={handleCancel} className="btn btn-ghost gap-2">
							<FiX className="w-4 h-4" />
							Cancel
						</button>
						<button 
							onClick={handleSave} 
							disabled={saving}
							className="btn btn-primary gap-2"
						>
							<FiSave className="w-4 h-4" />
							{saving ? 'Saving...' : 'Save Task'}
						</button>
					</div>
				</div>
			</div>

			{/* Error Display */}
			{error && (
				<div className="alert alert-error mx-6 mt-4">
					<span>{error}</span>
				</div>
			)}

			{/* Form */}
			<div className="flex-1 p-6 overflow-hidden">
				<div className="h-full grid grid-cols-2 gap-6">
					{/* Left Column - Task Details */}
					<div className="space-y-4">
						{/* Title */}
						<div className="form-control">
							<label className="label">
								<span className="label-text font-semibold">Title *</span>
							</label>
							<input
								type="text"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								className="input input-bordered w-full"
								placeholder="Enter task title..."
								required
							/>
						</div>

						{/* Description */}
						<div className="form-control">
							<label className="label">
								<span className="label-text font-semibold">Description</span>
							</label>
							<textarea
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								className="textarea textarea-bordered w-full"
								rows={4}
								placeholder="Enter task description..."
							/>
						</div>

{/* Priority and Due Date */}
					<div className="grid grid-cols-2 gap-4">
						<div className="form-control">
							<label className="label">
								<span className="label-text font-semibold">Priority</span>
							</label>
							<select
								value={priority}
								onChange={(e) => setPriority(e.target.value as any)}
								className="select select-bordered w-full"
							>
								<option value="low">Low</option>
								<option value="medium">Medium</option>
								<option value="high">High</option>
								<option value="urgent">Urgent</option>
							</select>
						</div>

						<div className="form-control">
							<label className="label">
								<span className="label-text font-semibold">Due Date</span>
							</label>
							<input
								type="date"
								value={dueDate}
								onChange={(e) => setDueDate(e.target.value)}
								className="input input-bordered w-full"
							/>
						</div>
					</div>

					{/* Status (when editing) */}
					{isEditing && (
						<div className="form-control">
							<label className="label">
								<span className="label-text font-semibold">Status</span>
							</label>
							<select
								value={status}
								onChange={(e) => setStatus(e.target.value as any)}
								className="select select-bordered w-full"
							>
								<option value="open">Open</option>
								<option value="in_progress">In Progress</option>
								<option value="completed">Completed</option>
								<option value="cancelled">Cancelled</option>
							</select>
						</div>
					)}

						{/* Patient Association */}
						<div className="form-control relative">
							<label className="label">
								<span className="label-text font-semibold">Patient</span>
							</label>
							{selectedPatient ? (
								<div className="flex items-center gap-2">
									<div className="flex-1 flex items-center gap-2 px-3 py-2 bg-primary text-primary-content border border-primary rounded">
										<FiUser className="w-4 h-4" />
										<span>
											{selectedPatient.first_name} {selectedPatient.last_name}
										</span>
										{selectedPatient.date_of_birth && (
											<span className="text-xs opacity-70">
												(DOB: {selectedPatient.date_of_birth})
											</span>
										)}
										<button
											onClick={clearPatientSelection}
											className="ml-auto opacity-70 hover:opacity-100"
											title="Clear patient"
										>
											<FiX className="w-4 h-4" />
										</button>
									</div>
								</div>
							) : (
								<input
									type="text"
									value={patientSearch}
									onChange={(e) => setPatientSearch(e.target.value)}
									className="input input-bordered w-full"
									placeholder="Search for patient..."
								/>
							)}

							{showPatientResults && patientResults.length > 0 && (
								<div className="absolute top-full left-0 mt-1 w-full bg-base-100 rounded-lg shadow-xl border border-base-300 max-h-60 overflow-y-auto z-50">
									{patientResults.map((patient) => (
										<div
											key={patient.id}
											onClick={() => handlePatientSelect(patient)}
											className="px-3 py-2 hover:bg-base-200 cursor-pointer border-b border-base-300 last:border-b-0"
										>
											<div className="font-medium">
												{patient.first_name} {patient.last_name}
											</div>
											<div className="text-xs opacity-60">
												DOB: {patient.date_of_birth || 'N/A'} | MRN: {patient.medical_record_number || 'N/A'}
											</div>
										</div>
									))}
								</div>
							)}
						</div>

						{/* Assign To */}
						<div className="form-control">
							<label className="label">
								<span className="label-text font-semibold">Assign To</span>
							</label>
							<select
								value={assignedToId || ''}
								onChange={(e) => setAssignedToId(e.target.value ? Number(e.target.value) : null)}
								className="select select-bordered w-full"
								disabled={loadingUsers}
							>
								<option value="">Unassigned</option>
								{users.map(user => (
									<option key={user.id} value={user.id}>
										{user.first_name && user.last_name
											? `${user.first_name} ${user.last_name} (${user.username})`
											: user.username}
									</option>
								))}
							</select>
						</div>
					</div>

					{/* Right Column - Attachments */}
					<div className="space-y-4">
						<div className="card bg-base-200 h-full flex flex-col">
						<div className="card-body">
							<h3 className="card-title text-lg">Attachments</h3>

							{/* Upload Buttons */}
							<div className="flex gap-2 mb-4">
								<button
									onClick={() => fileInputRef.current?.click()}
									className="btn btn-sm btn-primary gap-2"
								>
									<FiUpload className="w-4 h-4" />
									Upload from PC
								</button>
								<input
									ref={fileInputRef}
									type="file"
									multiple
									onChange={handleFileSelect}
									className="hidden"
								/>

								{selectedPatient && (
									<button
										onClick={() => setShowDocumentSelector(!showDocumentSelector)}
										className="btn btn-sm btn-secondary gap-2"
									>
										<FiFolder className="w-4 h-4" />
										Add from Patient File
									</button>
								)}
							</div>

								{/* Patient Document Selector */}
								{showDocumentSelector && selectedPatient && (
									<div className="bg-base-100 rounded-lg p-4 mb-4 flex-shrink-0">
									<div className="flex justify-between items-center mb-3">
										<h4 className="font-semibold">
											Patient Documents - {selectedPatient.first_name} {selectedPatient.last_name}
										</h4>
										<button
											onClick={() => setShowDocumentSelector(false)}
											className="btn btn-ghost btn-xs btn-circle"
										>
											<FiX className="w-4 h-4" />
										</button>
									</div>

									<input
										type="text"
										value={documentFilter}
										onChange={(e) => setDocumentFilter(e.target.value)}
										className="input input-bordered input-sm w-full mb-3"
										placeholder="Filter documents..."
									/>

									{loadingDocuments ? (
										<div className="text-center py-4">
											<span className="loading loading-spinner loading-sm"></span>
										</div>
									) : filteredDocuments.length === 0 ? (
										<div className="text-center py-4 opacity-60">
											No documents found
										</div>
									) : (
										<div className="max-h-60 overflow-y-auto space-y-2">
											{filteredDocuments.map((doc) => (
												<div
													key={doc.id}
													onClick={() => handleAddPatientDocument(doc)}
													className="flex items-center justify-between p-2 hover:bg-base-200 rounded cursor-pointer"
												>
													<div className="flex items-center gap-2">
														<FiFile className="w-4 h-4 opacity-50" />
														<div>
															<div className="text-sm font-medium">{doc.title}</div>
															<div className="text-xs opacity-60">
																{new Date(doc.document_date).toLocaleDateString()}
															</div>
														</div>
													</div>
													<button className="btn btn-ghost btn-xs">Add</button>
												</div>
											))}
										</div>
									)}
								</div>
							)}

							{/* Attachment List */}
							<div className="flex-1 overflow-y-auto">
								{attachments.length === 0 ? (
									<div className="text-center py-8 opacity-60">
										<FiPaperclip className="w-12 h-12 mx-auto mb-2 opacity-30" />
										<p>No attachments yet</p>
									</div>
								) : (
									<div className="space-y-2">
										{attachments.map((attachment, index) => {
										const isFile = attachment instanceof File;
										const isTaskAttachment = 'id' in attachment;

										return (
											<div
												key={index}
												className="flex items-center justify-between p-3 bg-base-100 rounded-lg"
											>
												<div className="flex items-center gap-3">
													<FiFile className="w-5 h-5 opacity-50" />
													<div>
														<div className="font-medium">
															{isFile
																? attachment.name
																: (attachment as TaskAttachment).file_name}
														</div>
														<div className="text-xs opacity-60">
															{isFile && formatFileSize(attachment.size)}
															{!isFile && (attachment as TaskAttachment).is_from_patient_file && (
																<span className="badge badge-info badge-xs">
																	From Patient File
																</span>
															)}
														</div>
													</div>
												</div>
												<div className="flex gap-2">
													{isTaskAttachment && (attachment as TaskAttachment).file_url && (
														<a
															href={(attachment as TaskAttachment).file_url}
															target="_blank"
															rel="noopener noreferrer"
															className="btn btn-ghost btn-sm btn-square"
															title="Download"
														>
															<FiDownload className="w-4 h-4" />
														</a>
													)}
													<button
														onClick={() => handleRemoveAttachment(index)}
														className="btn btn-ghost btn-sm btn-square text-error"
														title="Remove"
													>
														<FiTrash2 className="w-4 h-4" />
													</button>
												</div>
											</div>
										);
									})}
								</div>
							)}
						</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		</div>
	);
};

export default TaskCreate;


